<template>
  <EInstantAnswer
    class="local-businesses"
    data-test-id="local-businesses"
    :feedback-link="{
      ticketSubject: 'Search - Local Businesses',
      widgetType: 'local_businesses_widget'
    }"
    no-padding
    :sources="sources"
    powered-by-ecosia
  >
    <h2 class="sr-only">
      {{ $t('web.widgets.localbusinesses.title') }}
    </h2>
    <div class="local-businesses__layout">
      <div class="local-businesses__map" data-test-id="local-businesses-map">
        <EMapboxImage
          :marker-coordinates="markerCoordinates"
          :pin-color="pinColor"
          :numbered-pins="true"
          :screen-sizes="screenSizes"
          :url="tripadvisorMapLink"
          :place-types="['poi']"
          :extra-margin="true"
          :alt="$t('web.widgets.localbusinesses.maplink')"
          @map-link-click="trackClickEvent('map')"
        />
      </div>
      <EPoiList
        class="local-businesses__items"
        v-bind="$props"
        @poi-click="trackClickEvent('poi')"
        @link-click="trackClickEvent('link')"
      />
    </div>
  </EInstantAnswer>
</template>

<script>
import {
  trackImpression,
  trackClick,
  PAGE_SERP,
  TYPE_WIDGET,
} from '~~/search/common/js/client/analytics/rich-content';
import EInstantAnswer from '~/components/instant-answers/base';
import EMapboxImage from '~/components/mapbox-image';
import EPoiList from './poi-list';

export const TRACK_ID = 'local_businesses';

const sizes = {
  breakpoint: {
    desktop: 1024,
    tablet: 768,
    mobile: 225,
  },
  imageWidth: {
    desktop: 300,
    tablet: 360,
    mobile: 400,
  },
  imageHeight: { // key is the number of items on the list
    1: {
      desktop: 210,
      tablet: 420,
      mobile: 120,
    },
    2: {
      desktop: 310,
      tablet: 320,
      mobile: 120,
    },
    3: {
      desktop: 420,
      tablet: 430,
      mobile: 120,
    },
  },
};

export default {
  name: 'ELocalBusinessesWidget',
  components: {
    EInstantAnswer,
    EMapboxImage,
    EPoiList,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      pinColor: 'af1731', // dark red, not in DS
    };
  },
  computed: {
    sources() {
      return [{
        url: 'https://www.tripadvisor.com/',
        title: 'Tripadvisor',
      }];
    },
    markerCoordinates() {
      return this.items.map((item) => ({
        latitude: item.location.latitude,
        longitude: item.location.longitude,
        locationName: item.location.placeName,
      }));
    },
    screenSizes() {
      const markers = Math.min(3, this.markerCoordinates.length);
      return [
        {
          minWidth: sizes.breakpoint.desktop,
          imageSize: `${sizes.imageWidth.desktop}x${sizes.imageHeight[markers].desktop}`,
        },
        {
          minWidth: sizes.breakpoint.tablet,
          imageSize: `${sizes.imageWidth.tablet}x${sizes.imageHeight[markers].tablet}`,
        },
        {
          minWidth: sizes.breakpoint.mobile,
          imageSize: `${sizes.imageWidth.mobile}x${sizes.imageHeight[markers].mobile}`,
        },
      ];
    },
    tripadvisorMapLink() {
      return `${this.url}#MAPVIEW`;
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_WIDGET,
      once: false,
    });
  },
  methods: {
    trackClickEvent(target) {
      trackClick({
        name: TRACK_ID,
        type: TYPE_WIDGET,
        target,
      })();
    },
  },
};
</script>

<style lang="scss">
$map-height-mobile: 120px;

.local-businesses {
  overflow: hidden;
}

.local-businesses__layout {
  display: flex;
  flex-direction: column;
}

.local-businesses__map {
  position: relative;
  height: $map-height-mobile;
}

.local-businesses__items {
  padding: $space-m $space-m 0 $space-m;
}

@include tablet {
  .local-businesses__layout {
    flex-direction: row;
  }

  .local-businesses__map {
    flex-basis: 45%;
    order: 2;
    margin-bottom: -$space-1l; // Move bottom behind footer wave
    height: unset;
  }

  .local-businesses__items {
    flex-basis: 55%;
  }
}

@include desktop {
  .local-businesses__layout {
    flex-direction: row;
  }
}
</style>
